'use client';

import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { fadeInUp, viewportOnce } from '@/lib/animations';

export default function Hero() {
  const t = useTranslations('Merchants.Hero');

  const highlights = [
    {
      icon: (
        <svg className="w-5 h-5 color-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      ),
      titleKey: 'qrCodeTitle',
      descKey: 'qrCodeDesc',
    },
    {
      icon: (
        <svg className="w-5 h-5 color-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      titleKey: 'settlementTitle',
      descKey: 'settlementDesc',
    },
    {
      icon: (
        <svg className="w-5 h-5 color-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      titleKey: 'noChargebacksTitle',
      descKey: 'noChargebacksDesc',
    },
  ];

  return (
    <motion.section
      aria-labelledby="merchants-hero-heading"
      className="w-full bg-white py-12 sm:py-20 px-6 sm:px-10 lg:px-16 overflow-hidden"
      initial="hidden"
      whileInView="visible"
      viewport={viewportOnce}
      variants={fadeInUp}
    >
      <div className="max-w-8xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
         <div className="lg:col-span-6 space-y-6">
          <span className="hero-fade-up text-xs sm:text-sm font-semibold tracking-widest color-primary uppercase">
            {t('for_merchants')}
          </span>
          <h1 id="merchants-hero-heading" className="text-4xl sm:text-5xl lg:text-7xl font-semibold leading-[1.1]">
            {t.rich('title', {
              highlight: (chunks) => <span className="color-primary block">{chunks}</span>,
              br: () => <br />,
            })}
          </h1>
          <p className="text-base sm:text-lg text-gray-600 font-medium">
            {t('subtitle')}
          </p>

          <div className="pt-2">
            <Link
              href="#how-it-works"
              className="btn-blink-hover inline-flex items-center justify-center gap-2 bg-primary text-white text-sm font-bold px-7 py-3.5 rounded-full transition-all duration-200 shadow-md hover:shadow-lg active:scale-95"
            >
              {t('how_it_works')}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
          
          {/* <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-6 border-t border-gray-100">
            {highlights.map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <div className="p-2 bg-blue-50 rounded-xl shrink-0 mt-0.5">
                  {item.icon}
                </div>
                <div>
                  <h2 className="text-xs font-bold text-gray-900 leading-snug">
                    {t(item.titleKey)}
                  </h2>
                  <p className="text-[11px] text-gray-500 font-medium">
                    {t(item.descKey)}
                  </p>
                </div>
              </div>
            ))}
          </div> */}
        </div>

        <div className="lg:col-span-6 relative flex justify-center items-center">
          <div className="relative w-full aspect-[4/3] rounded-3xl rounded-br-none overflow-hidden shadow-2xl bg-gray-100">
            <Image
              src="/images/merchants/hero.webp"
              alt="Merchant accepting Kuik QR payment"
              fill
              priority
              sizes="(max-width: 1024px) 100vw, 50vw"
              className="object-cover"
            />
          </div>
        </div>

      </div>
    </motion.section>
  );
}